import { projectNameMap } from '@constants'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { HStack } from '@pets-ui/HStack/HStack'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { ArrowDownSmall, ArrowUpSmall, CloseLarge } from '@pets-ui/icons'
import { Image } from '@pets-ui/Image/Image'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { zIndex } from '@pets-ui/tokens/zIndex.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useCallback, useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom-v5'
import stmLogo from '../../assets/shepherdtech-logo.png'
import { BrandCategory, brandImageMap, type BrandInfo, HEADER_HEIGHT, NAV_LINKS } from '../../constants'
import { useStmBrandStore } from '../../hooks'

const styles = stylex.create({
  panel: {
    position: 'fixed',
    zIndex: zIndex.appBar,
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
})

interface BrandNavDrawerProps {
  open: boolean
  onClose: () => void
  onSelectBrand?: (brand: BrandInfo) => void
}

const BrandSection = (props: {
  label: string
  brands: BrandInfo[]
  onSelectBrand?: (brand: BrandInfo) => void
}) => {
  const { label, brands, onSelectBrand } = props
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <HStack
        p={3}
        alignItems="center"
        justifyContent="space-between"
        backgroundColor={colors.white}
        borderBottomColor={colors.grey300}
        borderBottomStyle="solid"
        borderBottomWidth={0.5}
        cursor="pointer"
        onClick={() => setExpanded(prev => !prev)}
      >
        <Text variant="subtitle" bold color={colors.grey800}>{label}</Text>
        {expanded
          ? <ArrowUpSmall fontSize={20} color={colors.grey600} />
          : <ArrowDownSmall fontSize={20} color={colors.grey600} />}
      </HStack>
      <Collapse in={expanded}>
        <VStack>
          {brands.map(brand => (
            <HStack
              key={brand.campaignCode}
              p={3}
              gap={2}
              alignItems="center"
              backgroundColor={colors.grey50}
              borderBottomColor={colors.grey300}
              borderBottomStyle="solid"
              borderBottomWidth={0.5}
              cursor="pointer"
              onClick={() => onSelectBrand?.(brand)}
            >
              <Image src={brandImageMap[brand.projectCode]?.avatar} alt={brand.projectCode} width={40} height={40} borderRadius={24} />
              <Text color={colors.grey800}>
                {projectNameMap[brand.projectCode] ?? brand.projectCode}
              </Text>
            </HStack>
          ))}
        </VStack>
      </Collapse>
    </>
  )
}

export const BrandNavDrawer = (props: BrandNavDrawerProps) => {
  const { open, onClose, onSelectBrand } = props
  const { brands } = useStmBrandStore()
  const history = useHistory()
  const { url: basePath } = useRouteMatch()

  const handleSelectBrand = useCallback((brand: BrandInfo) => {
    onSelectBrand?.(brand)
    onClose()
  }, [onSelectBrand, onClose])

  const petBrands = useMemo(() => brands.filter(brand => brand.category === BrandCategory.PET), [brands])
  const humanBrands = useMemo(() => brands.filter(brand => brand.category === BrandCategory.HUMAN), [brands])

  if (!open) return null

  return (
    <VStack
      backgroundColor={colors.grey50}
      overflowY="auto"
      rootStyle={styles.panel}
    >
      <HStack
        p={3}
        height={HEADER_HEIGHT}
        backgroundColor={colors.white}
        borderBottomColor={colors.grey100}
        borderBottomStyle="solid"
        borderBottomWidth={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Image src={stmLogo} alt="牧羊人經銷平台" width={80} height={32} objectFit="contain" />
        <IconButton onClick={onClose}>
          <CloseLarge fontSize={20} color={colors.grey600} />
        </IconButton>
      </HStack>

      {NAV_LINKS.map(item => (
        <HStack
          key={item.to}
          p={3}
          alignItems="center"
          backgroundColor={colors.white}
          borderBottomColor={colors.grey300}
          borderBottomStyle="solid"
          borderBottomWidth={0.5}
          cursor="pointer"
          onClick={() => {
            history.push(`${basePath}${item.to}`)
            onClose()
          }}
        >
          <Text variant="subtitle" bold color={colors.grey800}>{item.label}</Text>
        </HStack>
      ))}

      {petBrands.length > 0 && (
        <BrandSection
          label="寵食品牌"
          brands={petBrands}
          onSelectBrand={handleSelectBrand}
        />
      )}
      {humanBrands.length > 0 && (
        <BrandSection
          label="人食品牌"
          brands={humanBrands}
          onSelectBrand={handleSelectBrand}
        />
      )}
    </VStack>
  )
}
